import {
  Box,
  Grow,
  List,
  ListItemButton,
  Popper,
  Tooltip
} from '@mui/material';
import React from 'react';
import AddRelativeMenu from './options/AddRelativeMenu';
import GenderMenu from './options/GenderMenu';
import AdoptedMenu from './options/AdoptedMenu';
import FertilityMenu from './options/FertilityMenu';
import EditText from './options/EditText';
import Comment from './options/Comment';
import SetRelation from './options/SetRelation';
import CreateProxy from './options/CreateProxy';
import DeleteProxy from './options/DeleteProxy';
import DeleteNode from './options/DeleteNode';
import HidePerson from './options/HidePerson';
import ResetPosition from './options/ResetPosition';
import MakeProband from './options/MakeProband';
import RemoveProband from './options/RemoveProband';
import ReproductiveMenu from './options/ReproductiveMenu';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getProbandState } from '../pedigree/probandState';
import { reproMenuPolicy } from './reproMenuPolicy';

function ContextMenu({
  user,
  node,
  setMemberRecords,
  memberRecords,
  onCloseMenu,
  setPedigreeState,
  selectionModeRef,
  handleSelectionExit,
  setError,
  customPositions,
  setCustomPositions,
  selectedCount,
  onMergeTwoSelected,
  onAddProxy,
  onRemoveProxy,
  eligiblePids,
  onMigrate,
  isSaving = false,
  onHistoryCheckpoint = null,
  viewHide = null,
  hideAnalysisMembers = null
}) {
  const isMigratable = !!eligiblePids?.has(String(node?.pid));
  // Tri-state proband read (see pedigree/probandState.js): 'explicit'
  // promoted member, 'cleared' (no active proband → probandPid null), or
  // 'legacy' structural-root fallback. Deriving activeProbandPid from the
  // same selector Canvas uses keeps arrow presence ⇔ Remove Proband
  // availability in lockstep across all three states. NOTE (D2, accepted):
  // this makes virtually every root read as the current proband, so Delete
  // Person on the root is a deliberate two-step flow (move or remove the
  // proband first).
  const activeProbandPid = React.useMemo(
    () => getProbandState(memberRecords).probandPid,
    [memberRecords]
  );
  const isCurrentProband =
    activeProbandPid != null && String(node?.pid) === String(activeProbandPid);
  // Per-node action policy (Bennett repro nodes restrict the menu + relabel Delete/Hide/
  // Note by node type). Ordinary nodes + flag-off → full menu with "Person" labels.
  const policy = reproMenuPolicy(node);
  const [subMenu, setSubMenu] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  React.useEffect(() => {
    setSubMenu(false);
  }, [node]);

  const toggleSubMenu = (e, { val }) => {
    setAnchorEl(e.currentTarget);
    setSubMenu({ val });
  };

  const menu = React.useMemo(() => {
    switch (subMenu.val) {
      case 1:
        return (
          <AddRelativeMenu
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            onCloseMenu={onCloseMenu}
            setPedigreeState={setPedigreeState}
            user={user}
            // Adds stay disabled for the WHOLE save round trip (fire →
            // response-merge): a click landing mid-flight used to get its
            // structural edits reverted by the response reconciliation.
            isDisabled={isSaving}
          />
        );

      case 2:
        return (
          <SetRelation
            node={node}
            user={user}
            handleClose={onCloseMenu}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            setPedigreeState={setPedigreeState}
            selectionModeRef={selectionModeRef}
            handleSelectionExit={handleSelectionExit}
            setError={setError}
          />
        );

      case 3:
        return (
          <GenderMenu
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 4:
        return (
          <AdoptedMenu
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 5:
        return (
          <FertilityMenu
            node={node}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 6:
        return (
          <Comment
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 7:
        return (
          <EditText
            node={node}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      case 8:
        return node?.isProxy ? (
          <DeleteProxy
            node={node}
            handleClose={onCloseMenu}
            onRemoveProxy={onRemoveProxy}
            setError={setError}
          />
        ) : (
          <CreateProxy
            node={node}
            handleClose={onCloseMenu}
            memberRecords={memberRecords}
            onAddProxy={onAddProxy}
            setError={setError}
          />
        );

      case 9:
        return (
          <DeleteNode
            node={node}
            user={user}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
          />
        );

      case 10:
        return (
          <HidePerson
            node={node}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
            viewHide={viewHide}
            analysisMembers={hideAnalysisMembers}
          />
        );

      case 11:
        return (
          <ResetPosition
            node={node}
            customPositions={customPositions}
            setCustomPositions={setCustomPositions}
            handleClose={onCloseMenu}
            onHistoryCheckpoint={onHistoryCheckpoint}
          />
        );

      case 12:
        return (
          <MakeProband
            node={node}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
            userId={user?._id}
            isSaving={isSaving}
          />
        );

      case 13:
        return (
          <RemoveProband
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
            userId={user?._id}
            isSaving={isSaving}
          />
        );

      case 14:
        return (
          <ReproductiveMenu
            node={node}
            user={user}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            handleClose={onCloseMenu}
            setPedigreeState={setPedigreeState}
          />
        );

      default:
        return false;
    }
  }, [
    subMenu,
    node,
    memberRecords,
    user,
    customPositions,
    setCustomPositions,
    setMemberRecords,
    setPedigreeState,
    setError,
    onCloseMenu,
    onAddProxy,
    onRemoveProxy,
    isSaving
  ]);

  if (selectedCount > 2) {
    return (
      <ClickAwayListener onClickAway={onCloseMenu}>
        <Box sx={{ position: 'relative', zIndex: 1000 }}>
          {node && (
            <Grow in={node} style={{ transformOrigin: 'top bottom' }}>
              <Box
                sx={{
                  width: 170,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px #777 solid',
                  margin: 1
                }}
              >
                <List disablePadding>
                  <ListItemButton
                    onClick={() => {
                      onMigrate?.(node);
                      onCloseMenu?.();
                    }}
                  >
                    {`Migrate (${selectedCount})`}
                  </ListItemButton>
                </List>
              </Box>
            </Grow>
          )}
        </Box>
      </ClickAwayListener>
    );
  }

  return (
    <ClickAwayListener onClickAway={onCloseMenu}>
      <Box sx={{ position: 'relative', zIndex: 1000 }}>
        {node && (
          <Grow in={node} style={{ transformOrigin: 'top bottom' }}>
            <Box
              sx={{
                width: 170,
                bgcolor: 'white',
                borderRadius: 1,
                border: '1px #777 solid',
                margin: 1
              }}
            >
              {selectedCount === 2 ? (
                <List disablePadding>
                  <ListItemButton
                    onMouseEnter={() => setSubMenu(false)}
                    onClick={() => {
                      onMergeTwoSelected?.();
                      onCloseMenu?.();
                    }}
                  >
                    Merge
                  </ListItemButton>
                  <ListItemButton
                    onMouseEnter={() => setSubMenu(false)}
                    onClick={() => {
                      onMigrate?.(node);
                      onCloseMenu?.();
                    }}
                  >
                    Migrate (2)
                  </ListItemButton>
                </List>
              ) : node?.isProxy ? (
                <DeleteProxy
                  node={node}
                  handleClose={onCloseMenu}
                  onRemoveProxy={onRemoveProxy}
                  setError={setError}
                />
              ) : (
                <List disablePadding>
                  {/* Items with fly-out submenus — chevron, open on hover */}
                  {[
                    ...(policy.allow.addRelation
                      ? [{ val: 1, label: 'Add Relation' }]
                      : []),
                    ...(policy.allow.setRelation
                      ? [{ val: 2, label: 'Set Relation' }]
                      : []),
                    ...(policy.allow.proxy
                      ? [
                          {
                            val: 8,
                            label: node?.isProxy
                              ? 'Remove Proxy'
                              : 'Proxy Person'
                          }
                        ]
                      : []),
                    ...(policy.allow.gender
                      ? [{ val: 3, label: 'Gender' }]
                      : []),
                    ...(policy.allow.adopted
                      ? [{ val: 4, label: 'Adopted' }]
                      : []),
                    ...(policy.allow.fertility
                      ? [{ val: 5, label: 'Fertility' }]
                      : []),
                    // Reproductive (Bennett 2022): person → Add pregnancy; pregnancy →
                    // Edit + add donors; donor → hidden (no actions); flag-off → hidden.
                    ...(policy.allow.reproductive
                      ? [{ val: 14, label: 'Reproductive' }]
                      : [])
                  ].map(({ val, label }) => (
                    <ListItemButton
                      key={val}
                      selected={subMenu?.val === val}
                      onMouseEnter={(e) => toggleSubMenu(e, { val })}
                      onClick={(e) => toggleSubMenu(e, { val })}
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      {label}
                      <ChevronRightIcon
                        fontSize="small"
                        sx={{ ml: 1, opacity: 0.5 }}
                      />
                    </ListItemButton>
                  ))}
                  {/* Top-level actions — no chevron, click only, close submenu on hover.
                      Gated off for a derived reproductive node: these are member-based and
                      a pregnancy/donor is not a member (its actions live under Reproductive). */}
                  {policy.allow.note && (
                    <ListItemButton
                      onMouseEnter={() => setSubMenu(false)}
                      onClick={(e) => toggleSubMenu(e, { val: 6 })}
                    >
                      {`${policy.nounLabel} Note`}
                    </ListItemButton>
                  )}
                  {policy.allow.editText && (
                    <ListItemButton
                      onMouseEnter={() => setSubMenu(false)}
                      onClick={(e) => toggleSubMenu(e, { val: 7 })}
                    >
                      Edit Text
                    </ListItemButton>
                  )}
                  {policy.allow.hide && (
                    <ListItemButton
                      onMouseEnter={() => setSubMenu(false)}
                      onClick={(e) => toggleSubMenu(e, { val: 10 })}
                    >
                      {`Hide ${policy.nounLabel}`}
                    </ListItemButton>
                  )}
                  {/* Proband changes stay disabled for the WHOLE save round
                      trip (fire → response-merge), mirroring Add Relative
                      above: a promote/clear fired mid-flight races the
                      in-flight config save, which could clobber the
                      designation it just wrote. Disabled buttons swallow
                      events, so the Tooltip needs the span wrapper. */}
                  {policy.allow.makeProband && !isCurrentProband && (
                    <Tooltip
                      title={isSaving ? 'Saving — please wait' : ''}
                      placement="right"
                    >
                      <span style={{ display: 'block' }}>
                        <ListItemButton
                          disabled={isSaving}
                          onMouseEnter={() => setSubMenu(false)}
                          onClick={(e) => toggleSubMenu(e, { val: 12 })}
                        >
                          Make Proband
                        </ListItemButton>
                      </span>
                    </Tooltip>
                  )}
                  {policy.allow.makeProband && isCurrentProband && (
                    <Tooltip
                      title={isSaving ? 'Saving — please wait' : ''}
                      placement="right"
                    >
                      <span style={{ display: 'block' }}>
                        <ListItemButton
                          disabled={isSaving}
                          onMouseEnter={() => setSubMenu(false)}
                          onClick={(e) => toggleSubMenu(e, { val: 13 })}
                        >
                          Remove Proband
                        </ListItemButton>
                      </span>
                    </Tooltip>
                  )}
                  {policy.allow.resetPosition &&
                    customPositions?.has(node?.uid) && (
                      <ListItemButton
                        onMouseEnter={() => setSubMenu(false)}
                        onClick={(e) => toggleSubMenu(e, { val: 11 })}
                      >
                        Reset Position
                      </ListItemButton>
                    )}
                  {policy.allow.del && !isCurrentProband && (
                    <ListItemButton
                      onMouseEnter={() => setSubMenu(false)}
                      onClick={(e) => toggleSubMenu(e, { val: 9 })}
                    >
                      {`Delete ${policy.nounLabel}`}
                    </ListItemButton>
                  )}
                  {policy.allow.migrate && isMigratable && (
                    <ListItemButton
                      onMouseEnter={() => setSubMenu(false)}
                      onClick={() => {
                        onMigrate?.(node);
                        onCloseMenu?.();
                      }}
                    >
                      Migrate Person
                    </ListItemButton>
                  )}
                </List>
              )}
            </Box>
          </Grow>
        )}

        <Popper
          open={!!subMenu && !!node}
          anchorEl={anchorEl}
          placement="right-start"
          sx={{ zIndex: 1200 }}
        >
          <Grow in={node} style={{ transformOrigin: 'top bottom' }}>
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: 2,
                border: '1px #777 solid'
              }}
            >
              {menu}
            </Box>
          </Grow>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default ContextMenu;
